import React, { createContext, useContext, useEffect } from "react"
import { router, usePage } from "@inertiajs/react"
import { useNotificationFeed, type NotificationItem } from "@/hooks/use-notification-feed"
import { useDinerReload } from "@/features/user/use-diner-reload"

/**
 * Screens where a diner is actively inside a restaurant. The feed only connects
 * here: on /profile or /search there is nothing to react to, and an idle SSE
 * stream still costs a held connection per diner.
 *
 * `/dinein` covers the whole dine-in namespace with one prefix. This list is
 * prefix-matched and silently gave up realtime when the routes moved — the
 * screen still rendered, so nothing looked broken except that the order status
 * stopped changing under the diner. The Playwright suite is what caught it.
 */
const FEED_PATHS = ["/dashboard", "/dinein", "/invites", "/pickup", "/bills", "/orders"]

export const FAREWELL_PATH = "/visits/farewell"

type DinerRealtime = {
  notifications: NotificationItem[]
  connectionMode: "realtime" | "polling"
}

const EMPTY: DinerRealtime = { notifications: [], connectionMode: "realtime" }

const DinerRealtimeContext = createContext<DinerRealtime | null>(null)

/**
 * Owns the ONE notification connection for the diner app.
 *
 * It used to live inside the diner's notification bell, which meant the feed
 * existed only where a bell was rendered. Hoisting it here decoupled "are we
 * listening" from "is a bell on screen" — which is why removing that bell cost
 * the diner app nothing in realtime behaviour: the reloads below and the
 * farewell watcher are the feed's real consumers.
 */
export function DinerRealtimeProvider({ children }: { children: React.ReactNode }) {
  const { user } = usePage<{ user: unknown | null }>().props
  const currentPath = usePage().url
  const enabled =
    Boolean(user) &&
    (currentPath === "/" || FEED_PATHS.some((path) => currentPath.startsWith(path)))

  const feed = useNotificationFeed({
    feedPath: "/notifications/feed",
    streamPath: "/notifications/stream",
    enabled,
  })

  // The feed is the diner's only realtime wire: this turns its items into
  // partial reloads so orders and the bill stop going stale behind a manual
  // refresh.
  useDinerReload({
    notifications: feed.notifications,
    initialized: feed.initialized,
    path: currentPath,
  })

  return <DinerRealtimeContext.Provider value={feed}>{children}</DinerRealtimeContext.Provider>
}

export function useDinerRealtime(): DinerRealtime {
  return useContext(DinerRealtimeContext) ?? EMPTY
}

/** A closure older than this is history, not news. */
const FRESH_CLOSURE_MS = 90_000

const handledKey = (notificationId: number) => `farewell-handled:${notificationId}`

// sessionStorage, not a ref: Inertia remounts the layout on every navigation, so a
// ref would forget and re-fire. Throws in private mode on some browsers, hence the
// guards — losing the memo degrades to the freshness check, never to a crash.
function alreadyHandled(notificationId: number): boolean {
  try {
    return window.sessionStorage.getItem(handledKey(notificationId)) === "1"
  } catch {
    return false
  }
}

function markHandled(notificationId: number) {
  try {
    window.sessionStorage.setItem(handledKey(notificationId), "1")
  } catch {
    // Private mode: fall back to the freshness window alone.
  }
}

/**
 * Staff closed the bill: the visit is over, so take the diner to the goodbye.
 *
 * Two guards keep this from looping. The feed's first payload is HISTORY, and
 * acting on it would ping-pong — an already-acknowledged farewell redirects to the
 * dashboard, which reloads the same history, which navigates again — so a closure
 * only counts while it is fresh, and each one is handled at most once per tab.
 * Closures that predate the page load are caught server-side by
 * User::PendingFarewell instead, which is the layer that cannot miss.
 */
export function VisitClosedWatcher() {
  const { notifications } = useDinerRealtime()
  const { diner } = usePage<{ diner?: { billId?: number | null } | null }>().props
  const currentPath = usePage().url

  const activeBillId = diner?.billId ?? null
  const candidate = notifications.find((notification) => {
    if (notification.kind !== "bill_closed") return false
    // A diner keeps claims on older bills; only their current one ends the visit.
    if (activeBillId !== null && notification.aggregateId !== activeBillId) return false

    const age = Date.now() - new Date(notification.occurredAt).getTime()
    return age >= 0 && age < FRESH_CLOSURE_MS
  })

  useEffect(() => {
    if (!candidate) return
    if (currentPath.startsWith(FAREWELL_PATH)) return
    if (alreadyHandled(candidate.id)) return

    markHandled(candidate.id)
    router.visit(FAREWELL_PATH)
  }, [candidate, currentPath])

  return null
}
